import React, { useEffect, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import AgentActivityGraph from '../common/AgentActivityGraph';
import useStageJob from '../common/useStageJob';
import {
  getProject,
  setStageData,
  patchStage,
  subscribe,
  getAwsConfig,
} from '../../../services/pipelineProject.service';
import { runPipelineStage } from '../../../services/pipeline.service';
import '../Pipeline.css';

/**
 * Requirements Stage
 *
 * 1. User enters requirements text.
 * 2. Creator + Validator agents analyze and return:
 *    - Project overview, features, risks
 *    - THREE AWS solution tracks: minimal / moderate / best
 * 3. Budget preference from onboarding pre-selects a track; user can change it.
 * 4. The selected track drives Design, Code, CI/CD and Deployment stages.
 */
const BUDGET_TO_TRACK = {
  minimal:  'minimalSolution',
  moderate: 'moderateSolution',
  high:     'bestSolution',
};

function RequirementsStage() {
  const [project, setProject]   = useState(getProject());
  const [input, setInput]       = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [error, setError]       = useState(null);
  const [activity, setActivity] = useState([]);

  // Default track comes from saved AWS budget preference
  const awsCfgDefault = getAwsConfig();
  const defaultTrack  = BUDGET_TO_TRACK[awsCfgDefault?.budget] || 'moderateSolution';
  const [selectedTrack, setSelectedTrack] = useState(defaultTrack);

  useEffect(() => {
    const p = getProject();
    const stage = p.stages?.requirements;
    if (stage?.data?.input) setInput(stage.data.input);
    if (stage?.data) {
      setAnalysis(stage.data);
      if (stage.data.selectedSolution) {
        setSelectedTrack(stage.data.selectedSolution);
      } else {
        // First load — default from budget
        const cfg = getAwsConfig();
        setSelectedTrack(BUDGET_TO_TRACK[cfg?.budget] || 'moderateSolution');
      }
    }
    return subscribe(setProject);
  }, []);

  const handleResult = (result) => {
    const data = { ...(result || {}), input };
    setAnalysis(data);
    setStageData('requirements', data);
    setError(null);
  };

  const handleActivity = (acts) => setActivity(acts || []);

  const { polling, error: pollError, startPolling } = useStageJob(handleResult, setError, handleActivity);

  const handleAnalyze = async () => {
    if (!input.trim()) return;
    setError(null);
    setActivity([]);
    const comment = project?.stages?.requirements?.comment || '';
    const awsConfig = getAwsConfig() || {};
    try {
      const res = await runPipelineStage('requirements', { requirements: input, awsConfig }, comment);
      if (res.status === 'accepted') {
        patchStage('requirements', { jobId: res.jobId });
        startPolling(res.jobId);
      } else if (res.status === 'completed') {
        handleResult(res.result);
        if (res.agentActivity) handleActivity(res.agentActivity);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to analyze requirements.');
    }
  };

  const handleTrackSelect = (track) => {
    setSelectedTrack(track);
    if (analysis) {
      const updated = { ...analysis, selectedSolution: track };
      setAnalysis(updated);
      setStageData('requirements', updated);
    }
  };

  const stageState   = project?.stages?.requirements;
  const hasAnalysis  = Boolean(analysis?.title);
  const displayError = error || pollError;
  const awsConfig    = getAwsConfig();
  const hasTripleTrack = Boolean(analysis?.minimalSolution || analysis?.moderateSolution || analysis?.bestSolution);
  // backwards compat with old 2-solution responses
  const hasDualTrack   = !hasTripleTrack && Boolean(analysis?.costEffectiveSolution || analysis?.bestSolution);

  return (
    <PipelineShell
      stageId="requirements"
      description="Describe what you want to build. AI agents analyze and propose three AWS solution tracks."
      canApprove={hasAnalysis}
      headerAction={
        <button
          className="pipeline-btn primary"
          onClick={handleAnalyze}
          disabled={polling || !input.trim()}
        >
          {polling ? '⏳ Analyzing…' : analysis ? '↩ Re-analyze' : '🔍 Analyze Requirements'}
        </button>
      }
    >
      {!stageState?.approved && (
        <div className="pipeline-card">
          <div className="pipeline-card-title">Describe your project requirements</div>
          {awsConfig && (
            <div style={{ marginBottom: 10, padding: '6px 12px', background: '#f0fdf4', borderRadius: 6, fontSize: '0.8rem', color: '#166534', border: '1px solid #bbf7d0' }}>
              ☁️ Targeting <strong>{awsConfig.region}</strong> · {awsConfig.environment} · {awsConfig.budget} budget preference
            </div>
          )}
          <textarea
            className="pipeline-req-textarea"
            placeholder={`Example:\n• Build a REST API for a task management app\n• Users can sign up, log in, create tasks and assign them to other users\n• Tasks have priority (high / medium / low), due dates, and status\n• Send email notifications when tasks are assigned\n• Deploy on AWS`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={polling || stageState?.approved}
          />
        </div>
      )}

      {(polling || activity.length > 0) && (
        <AgentActivityGraph activity={activity} polling={polling} />
      )}

      {polling && (
        <div className="pipeline-loading">
          <div className="pipeline-spinner" />
          <p>AI agents are analyzing your requirements…</p>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Creator builds · Validator reviews · ~20s</p>
        </div>
      )}

      {displayError && !polling && <div className="pipeline-error">{displayError}</div>}

      {hasAnalysis && !polling && (
        <AnalysisOutput
          analysis={analysis}
          selectedTrack={selectedTrack}
          onSelectTrack={handleTrackSelect}
          approved={stageState?.approved}
          hasTripleTrack={hasTripleTrack}
          hasDualTrack={hasDualTrack}
        />
      )}
    </PipelineShell>
  );
}

// ── Solution Track Card ───────────────────────────────────────────────

const TRACK_META = {
  minimalSolution:  { emoji: '🟢', label: 'Minimal',  accent: '#10b981', sub: 'Cost-optimized' },
  moderateSolution: { emoji: '🟡', label: 'Moderate', accent: '#f59e0b', sub: 'Balanced'        },
  bestSolution:     { emoji: '🔴', label: 'Best',     accent: '#6366f1', sub: 'Max performance' },
  // backwards compat
  costEffectiveSolution: { emoji: '🟢', label: 'Cost-Effective', accent: '#10b981', sub: 'Cost-optimized' },
};

function SolutionTrackCard({ solution, trackKey, isSelected, onSelect, disabled }) {
  if (!solution) return null;
  const meta   = TRACK_META[trackKey] || { emoji: '⚪', label: trackKey, accent: '#64748b', sub: '' };
  const accent = meta.accent;

  return (
    <div
      onClick={disabled ? undefined : () => onSelect(trackKey)}
      style={{
        border: `2px solid ${isSelected ? accent : '#e2e8f0'}`,
        borderRadius: 10, padding: '16px 18px',
        cursor: disabled ? 'default' : 'pointer',
        background: isSelected ? `${accent}0d` : '#fff',
        transition: 'all 0.2s', flex: 1, minWidth: 200,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
            {meta.emoji} {solution.name || meta.label}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>{meta.sub}</div>
        </div>
        {isSelected && (
          <span style={{ fontSize: '0.7rem', background: accent, color: '#fff', padding: '2px 8px', borderRadius: 20, fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 8 }}>
            Selected ✓
          </span>
        )}
      </div>
      <p style={{ fontSize: '0.82rem', color: '#475569', margin: '0 0 8px', lineHeight: 1.5 }}>{solution.description}</p>
      {solution.monthlyEstimate && (
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: accent, marginBottom: 8 }}>💰 {solution.monthlyEstimate}</div>
      )}
      {Array.isArray(solution.techStack) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {solution.techStack.slice(0, 4).map((t, i) => (
            <span key={i} className="pipeline-tag gray" style={{ fontSize: '0.7rem' }}>
              {t.technology || t.tech}
            </span>
          ))}
        </div>
      )}
      {solution.tradeoffs && (
        <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic', marginTop: 4 }}>↔ {solution.tradeoffs}</div>
      )}
    </div>
  );
}

// ── Full Analysis Output ───────────────────────────────────────────────

function AnalysisOutput({ analysis, selectedTrack, onSelectTrack, approved, hasTripleTrack, hasDualTrack }) {
  const complexityColor = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };
  const cc = analysis.complexity?.toLowerCase();

  // Build the list of tracks to render
  const tracks = hasTripleTrack
    ? [
        { key: 'minimalSolution',  sol: analysis.minimalSolution  },
        { key: 'moderateSolution', sol: analysis.moderateSolution },
        { key: 'bestSolution',     sol: analysis.bestSolution     },
      ]
    : hasDualTrack
    ? [
        { key: 'costEffectiveSolution', sol: analysis.costEffectiveSolution },
        { key: 'bestSolution',          sol: analysis.bestSolution          },
      ]
    : [];

  return (
    <>
      <div className="pipeline-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="pipeline-analysis-title">{analysis.title}</div>
            <p className="pipeline-analysis-overview">{analysis.overview}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {cc && (
              <span className="pipeline-tag" style={{ background: `${complexityColor[cc]}15`, color: complexityColor[cc] }}>
                {cc} complexity
              </span>
            )}
            {analysis.estimatedTimeline && <span className="pipeline-tag gray">{analysis.estimatedTimeline}</span>}
          </div>
        </div>
        {Array.isArray(analysis.objectives) && analysis.objectives.length > 0 && (
          <>
            <div className="pipeline-card-title" style={{ marginTop: 14 }}>Objectives</div>
            <ul className="pipeline-list">{analysis.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
          </>
        )}
      </div>

      {tracks.length > 0 && (
        <div className="pipeline-card">
          <div className="pipeline-card-title" style={{ marginBottom: 4 }}>
            ☁️ AWS Solution Options
          </div>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 12 }}>
            All three solutions are generated. Select the one you want to take forward — it drives Design, Code, CI/CD and Deployment.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {tracks.map(({ key, sol }) => (
              <SolutionTrackCard
                key={key}
                solution={sol}
                trackKey={key}
                isSelected={selectedTrack === key}
                onSelect={onSelectTrack}
                disabled={approved}
              />
            ))}
          </div>
          {!approved && (
            <div style={{ marginTop: 8, fontSize: '0.76rem', color: '#94a3b8' }}>
              Your budget preference pre-selected a track. You can switch at any time before approving.
            </div>
          )}
        </div>
      )}

      {Array.isArray(analysis.features) && analysis.features.length > 0 && (
        <div className="pipeline-card">
          <div className="pipeline-card-title">Features</div>
          <div className="pipeline-grid-2">
            {analysis.features.map((f, i) => {
              const pc = { high: 'red', medium: 'yellow', low: 'green' }[f.priority?.toLowerCase()] || 'gray';
              return (
                <div key={i} style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: '0.88rem' }}>{f.name}</strong>
                    {f.priority && <span className={`pipeline-tag ${pc}`}>{f.priority}</span>}
                  </div>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pipeline-grid-2">
        {Array.isArray(analysis.constraints) && analysis.constraints.length > 0 && (
          <div className="pipeline-card">
            <div className="pipeline-card-title">Constraints</div>
            <ul className="pipeline-list">{analysis.constraints.map((c, i) => <li key={i}>{c}</li>)}</ul>
          </div>
        )}
        {Array.isArray(analysis.risks) && analysis.risks.length > 0 && (
          <div className="pipeline-card">
            <div className="pipeline-card-title">Risks</div>
            <ul className="pipeline-list">{analysis.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        )}
      </div>
    </>
  );
}

export default RequirementsStage;
